'use client'

import { getBafad, searchBatchBafads } from "@/api/actions"
import { environments, getNextSessions } from "@/services/holidayStay"
import { createObjectPagePathname, getMediaUrl } from "@/services/pathname"
import { useSearchParams } from "next/navigation"
import { useEffect, useRef, useState } from "react"
import MapComponent from "@/components/ui/map/Map"
import { status } from "@/services/status"
import Loading from "@/components/loading/Loading"
import { levels } from "@/services/bafad"

type MapProps = {
    aroeven: Aroeven,
    contextSearchParams: any,
}

export default function Map({
    aroeven,
    contextSearchParams
}: MapProps) {

    const searchParams = useSearchParams()

    const [map, setMap] = useState<google.maps.Map>(null)
    const [results, setResults] = useState<BafadBatch[]>(null)
    const [markers, setMarkers] = useState<any[]>([])
    const [loadingDataStatus, setLoadingDataStatus] = useState(status.idle)
    const [historyState, setHistoryState] = useState<any>(null)
    const [firstRender, setFirstRender] = useState<boolean>(true)
    const autoBounds = useRef<boolean>(false)

    const initResults = useRef<boolean>(false)

    const defaultBounds = { south: 43.126365452270235, west: -8.67604908273486, north: 50.06158816163058, east: 14.400768082734906 }

    async function fetchData() {

        autoBounds.current = false

        setLoadingDataStatus(status.loading)
        setMarkers([])
        setResults([])

        let page: number = 1
        let fetch: boolean = true
        let newResults: BafadBatch[] = []
        while (fetch === true) {
            try {
                const { items } = await searchBatchBafads(aroeven, {
                    ...contextSearchParams,
                    ...Object.fromEntries(searchParams),
                    batch: 1,
                    page
                })
                page = page + 1
                if (items.length === 0) {
                    fetch = false
                }
                newResults = [
                    ...newResults,
                    ...items
                ]
            }
            catch (error) {
                fetch = false
                console.log(error, 'error')
            }
        }
        setResults(newResults)
        setLoadingDataStatus(status.idle)
    }

    function setBounds() {
        console.log('setBounds')
        if (map === null) return
        let bounds = { ...defaultBounds }
        const availableMarkers = markers.filter(m => m.position.latitude && m.position.longitude)
        if (markers.length > 0) {
            bounds = {
                north: Math.max(...(availableMarkers as any[]).map(m => m.position.latitude)),
                east: Math.max(...(availableMarkers as any[]).map(m => m.position.longitude)),
                south: Math.min(...(availableMarkers as any[]).map(m => m.position.latitude)),
                west: Math.min(...(availableMarkers as any[]).map(m => m.position.longitude))
            }
        }
        map.fitBounds(bounds)
    }

    useEffect(() => {
        autoBounds.current = !!window?.history?.state?.results_map?.map
    }, [])

    useEffect(() => {
        setHistoryState(window?.history?.state?.results_map)
    }, [])

    useEffect(() => {
        if (window?.history?.state?.results_map?.results) {
            setResults(window.history.state.results_map.results)
            return
        }
        fetchData()
    }, [])

    useEffect(() => {
        if (initResults.current === false) {
            initResults.current = true
            return
        }
        fetchData()
    }, [searchParams, contextSearchParams])

    useEffect(() => {
        if (results === null) return
        console.log('use effect results', results)
        setMarkers(results.filter(b => b.latitude && b.longitude).map((b) => ({
            position: {
                latitude: b.latitude,
                longitude: b.longitude
            },
            title: levels[b.level]?.name ?? 'Bafa',
            infowindow: {
                content: `<div class="vignette map-info d-flex">
                                            <div style="background-color: #EEEEEE; min-height: 100px; min-width: 100px"></div>
                                            <div class="content mapItem__content d-flex flex-column justify-between ml-15">
                                              <div>
                                                <h4 class="fw-700 fs-5">` + (levels[b.level]?.name ?? 'Bafa') + `</h4>` +
                    `<p>Chargement...</p>` +
                    `</div>
                                            </div>
                                            <button class="gclose gbtn justify-content-start" tabindex="2" aria-label="Close"><i class="fi fi-rr-cross text-primary-3"></i></button>
                                          </div>`,
                fetchContent: async () => {
                    const bafad = await getBafad(aroeven, b.id)
                    return `<div class="vignette map-info d-flex">
                                            <img src=` + getMediaUrl(bafad.medias.filter(media => media.mimeType.indexOf('image/') === 0)[0]?.url ?? '#') + ` alt="image" class="mapItem__img size-100" loading="lazy" />
                                            <div class="content mapItem__content d-flex flex-column justify-between ml-15">
                                              <div>
                                                <h4 class="fw-700 fs-5">` + (levels[b.level]?.name ?? 'Bafa') + `</h4>` //levels[bafad.level].name
                        + `<div class="lieu d-flex items-center">` + bafad.accomodation.address.city + ` (` + bafad.accomodation.address.postCode.slice(0, 2) + `)</div>` +
                        (bafad.level === 'bafa_qualification' && bafad.thematicLabel ? (`<div class="d-flex items-center">
                                                  <div class="type">` + bafad.thematicLabel + `</div>
                                                </div>`) : ``) +
                        `<div class="tarif">à partir de ` + Math.floor(Math.min(...bafad.accomodationOptions.map(option => option.price)) / 100) + `€ par participant</div>
                                              </div>
                                            </div>
                                            <button class="gclose gbtn justify-content-start" tabindex="2" aria-label="Close"><i class="fi fi-rr-cross text-primary-3"></i></button>
                                          </div>`
                },
                target: createObjectPagePathname('Bafad', { id: b.id } as Bafad)
            },
            object: b
        })))
    }, [results])

    useEffect(() => {
        if (results === null) return
        try {
            window.history.replaceState({
                ...history.state,
                results_map: {
                    ...(window.history.state.results_map && { ...window.history.state.results_map }),
                    results
                },
                activeObject: null
            }, '')
        }
        catch (error) { }
    }, [results])

    useEffect(() => {
        if (map === null) return
        const listener = map.addListener('idle', () => {
            if (map.getCenter()) {
                try {
                    window.history.replaceState({
                        ...window.history.state,
                        results_map: {
                            ...(window.history.state.results_map && { ...window.history.state.results_map }),
                            map: {
                                zoom: map.getZoom(),
                                center: {
                                    latitude: map.getCenter().lat(),
                                    longitude: map.getCenter().lng()
                                }
                            }
                        }
                    }, '')
                }
                catch (error) {
                    console.log(error)
                }
            }
        })

        return () => {
            google.maps.event.removeListener(listener)
        }
    }, [map])

    useEffect(() => {
        if (!map) return
        if (autoBounds.current === true) return
        setBounds()
    }, [map, markers])

    useEffect(() => {
        setFirstRender(false)
    }, [])

    return (
        <>
            <div id="map" tabIndex={-1}>
                {firstRender === false &&
                    <MapComponent
                        {...historyState?.map?.center ? {
                            center: {
                                latitude: historyState.map.center.latitude,
                                longitude: historyState.map.center.longitude
                            }
                        } : {
                            boundaries: defaultBounds
                        }}
                        {...historyState?.map?.zoom && {
                            zoom: historyState.map.zoom
                        }}
                        markers={markers}
                        clustering={true}
                        setMap={setMap}
                        setMappedMarkers={(markers: any[]) => {
                            if (historyState?.activeObject) {
                                const marker = markers.filter(marker => marker.marker.object.id === historyState.activeObject.id)[0]
                                if (marker) {
                                    new google.maps.event.trigger(marker.gmMarker, 'click')
                                }
                            }
                        }}
                        setActiveObject={(object: Searchable) => {
                            console.log(object)
                            try {
                                window.history.replaceState({
                                    ...window.history.state,
                                    results_map: {
                                        ...(window.history.state.results_map && { ...window.history.state.results_map }),
                                        activeObject: object
                                    }
                                }, '')
                            }
                            catch (error) { }
                        }}
                    />
                }
            </div>
            {(map === null || loadingDataStatus === status.loading) &&
                <div style={{ position: 'absolute', top: '30%', left: '50%', backgroundColor: '#FFFFFF', borderRadius: '20px' }}>
                    <Loading />
                </div>
            }
        </>
    )
}